import {
  ViImageData,
  ViAggregatedPartDamage,
  ViCorePostImageResponse,
  Filter,
  ViImagePartDamage,
  ViImagePart,
  ViImageDamage,
  ViCoreProcessing,
  ViProcessingResult,
  ViCoreImagePartDamage,
  ViClaimDamagedPart,
  ViPhysicalPart,
  ViWorkAction,
  ViOperationId,
  ViWorkActionLabel,
} from "../models/";
import { ViState } from "../contexts/ViContext";
import imageCompression from "browser-image-compression";
import imageType from "image-type";
import { Dispatch, SetStateAction, useEffect, useState } from "react";

export const debounce = <F extends (...args: any[]) => any>(
  func: F,
  interval: number = 100
) => {
  let timer: ReturnType<typeof setTimeout>;

  return (...args: Parameters<F>): any => {
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => {
      func(...args);
    }, interval);
  };
};

export const hasMatchingDamage = (
  filter: Filter,
  damages: ViImagePartDamage[]
): boolean => {
  for (const damage of damages) {
    if (isMatchingDamage(filter, damage)) return true;
  }
  return false;
};

export const getFilterProperties = (
  damage: (ViImagePartDamage | ViAggregatedPartDamage | Filter)[]
): Filter[] =>
  damage.map((damage) => {
    if ("imageDamage" in damage) {
      const { genericPartName, side } = damage.aggregatedPartDamage.partIdentifiers;
      return {
        genericPartName,
        side,
        damageType: damage.imageDamage.type,
        aggPartDamageId: damage.aggregatedPartDamage.id,
        imgPartDamageId: damage.id,
      };
    } else if ("imagePartDamages" in damage) {
      const {
        damageType,
        partIdentifiers: { genericPartName, side },
      } = damage;
      return {
        genericPartName,
        side,
        damageType,
        aggPartDamageId: damage.id,
      };
    } else {
      const {
        genericPartName,
        side,
        damageType,
        aggPartDamageId,
        imgPartDamageId,
      } = damage;
      return {
        ...(genericPartName ? { genericPartName } : {}),
        ...(side ? { side } : {}),
        ...(damageType ? { damageType } : {}),
        ...(aggPartDamageId ? { aggPartDamageId } : {}),
        ...(imgPartDamageId ? { imgPartDamageId } : {}),
      };
    }
  });

export const isMatchingDamage = (
  filter: Filter,
  damage: Filter | ViImagePartDamage | ViAggregatedPartDamage
): boolean | undefined => {
  if (!filter) return undefined;
  const damageProperties = getFilterProperties([damage])[0];
  const filterProperties = getFilterProperties([filter])[0];
  const keys = Object.keys(filterProperties) as (keyof Filter)[];
  if (keys.length <= 1) return undefined;
  return keys
    .map(
      (key) =>
        damageProperties[key] &&
        filterProperties[key] &&
        damageProperties[key] === filterProperties[key]
    )
    .filter((value) => value !== undefined)
    .every(Boolean);
};

export const compressImage = async (
  file: File
): Promise<{ dataUrl: string; file: File; isCompressed: boolean }> => {
  const maxRes = parseInt(process.env.REACT_APP_MAX_IMAGE_MP || "5") * 1000000;
  const mime = await imageType(new Uint8Array(await file.arrayBuffer()))?.mime;

  const canvas: [
    ImageBitmap | HTMLImageElement,
    HTMLCanvasElement | OffscreenCanvas
  ] = await imageCompression.drawFileInCanvas(file);

  const orientation = await imageCompression.getExifOrientation(file);
  const res = canvas[0].height * canvas[0].width;
  if (mime === "image/jpeg" && res < maxRes && orientation <= 1) {
    return {
      dataUrl: await imageCompression.getDataUrlFromFile(file),
      file,
      isCompressed: false,
    };
  }

  const aspectRatio = canvas[0].width / canvas[0].height;
  const maxWidth = Math.min(Math.sqrt(maxRes * aspectRatio), 6000);
  const maxHeight = Math.min(Math.sqrt(maxRes / aspectRatio), 6000);
  const options = {
    maxWidthOrHeight: Math.round(Math.max(maxWidth, maxHeight)),
    fileType: "image/jpeg", //force jpeg because this lib adds an alpha channel to pngs
  };

  const compressedFile: File = await imageCompression(file, options);

  return {
    dataUrl: await imageCompression.getDataUrlFromFile(compressedFile),
    file: compressedFile,
    isCompressed: true,
  };
};

export const parsePostImageResponse = (
  image: ViImageData,
  postImageResponse: ViCorePostImageResponse
): ViImageData => {
  const imageResult = postImageResponse.images[0];
  return {
    ...image,
    id: imageResult.id,
    status: imageResult.status,
    ...(imageResult.metaData.filename ? { filename: imageResult.metaData.filename } : {}),
    dimensions: imageResult.metaData.dimensions,
    warnings: imageResult.warnings,
    error: imageResult.error,
    category: imageResult?.results?.category,
    isDuplicate: imageResult.error?.code === "I409.4" || false,
  };
};

export const getMean = (values: number[]) =>
  values.length
    ? values.reduce((sum, value) => (sum += value), 0) / values.length
    : undefined;

export const parseProcessingResponse = (
  state: ViState,
  response: ViCoreProcessing
): ViProcessingResult => {
  try {
    let imageDamagesObj: Record<string, ViImageDamage | undefined> = {};
    let imagePartsObj: Record<string, ViImagePart | undefined> = {};
    let imagePartDamagesObj: Record<string, ViCoreImagePartDamage | undefined> = {};
    for (const image of response.images) {
      var { imageParts, imageDamages, imagePartDamages } = image.results;
      imageDamagesObj = {
        ...imageDamagesObj,
        ...Object.fromEntries(
          imageDamages.map((damage) => [damage.id, { ...damage, imageId: image.id }])
        ),
      };
      imagePartsObj = {
        ...imagePartsObj,
        ...Object.fromEntries(
          imageParts.map((damage) => [damage.id, { ...damage, imageId: image.id }])
        ),
      };
      imagePartDamagesObj = {
        ...imagePartDamagesObj,
        ...Object.fromEntries(
          imagePartDamages.map((damage) => [damage.id, { ...damage, imageId: image.id }])
        ),
      };
    }

    const claimDamagedParts: ViClaimDamagedPart[] = response.claimDamagedParts.map(
      ({ physicalParts, objectDetectionLabels, ...cDP }) => ({
        ...cDP,
        physicalParts: physicalParts.map(({ operationResults, ...pP }) => {
          const operationIdToWorkAction: Record<
            keyof typeof ViOperationId | any,
            keyof typeof ViWorkActionLabel
          > = {
            E: "REPLACE",
            L: "PAINT",
            EC: "REPLACE",
            I: "REPAIR",
            BR: "REPAIR",
            H: "REPAIR",
            LE: "PAINT",
            N: "REMOVE-REFIT",
            M: "OTHER",
            LI1: "PAINT",
            LE1: "PAINT",
            D: "REPAIR",
            LI: "PAINT",
            LS: "PAINT",
            P: "OTHER",
          };

          const workActionsObj: Record<
            ViWorkActionLabel,
            ViWorkAction
          > = operationResults?.reduce((workActionsObj: any, { operationId, p }) => {
            const workAction = operationIdToWorkAction[operationId];
            workActionsObj[workAction] = {
              workAction,
              p: workActionsObj[workAction]
                ? Math.min(1, (workActionsObj[workAction].p += p))
                : p,
              operations: [
                ...(workActionsObj[workAction]
                  ? workActionsObj[workAction].operations
                  : []),
                { operationId, p },
              ],
            };
            return workActionsObj;
          }, {});

          const workActionResults = Object.values(workActionsObj);

          return { ...pP, workActionResults };
        }),
        aggregatedPartDamages: [],
      })
    );

    const aggregatedPartDamages: ViAggregatedPartDamage[] = response.aggregatedPartDamages.reduce(
      (aggregatedPartDamages, aPD) => {
        const {
          type,
          identifiers,
          imagePartDamageIds,
          claimDamagedPartId,
          ...damage
        } = aPD;
        let aggregatedPartDamage = {} as ViAggregatedPartDamage;
        const claimDamagedPart = claimDamagedParts.find(
          ({ id }) => id === claimDamagedPartId
        );
        if (!claimDamagedPart) return aggregatedPartDamages;
        Object.assign(aggregatedPartDamage, {
          ...damage,
          damageType: type,
          partIdentifiers: identifiers,
          claimDamagedPart,
          imagePartDamages: imagePartDamageIds.reduce((imagePartDamages, id) => {
            const iPD = imagePartDamagesObj[id];
            if (iPD) {
              const { imagePartId, imageDamageId, ...imagePartDamage } = iPD;
              var imagePart = imagePartsObj[imagePartId];
              var imageDamage = imageDamagesObj[imageDamageId];
              if (imagePart && imageDamage && aPD)
                return [
                  ...imagePartDamages,
                  {
                    ...imagePartDamage,
                    aggregatedPartDamage,
                    imagePart,
                    imageDamage,
                  },
                ];
            }
            return imagePartDamages;
          }, [] as ViImagePartDamage[]),
        });
        return [...aggregatedPartDamages, aggregatedPartDamage];
      },
      [] as ViAggregatedPartDamage[]
    );

    const expandedImagePartDamages = aggregatedPartDamages.flatMap((damage) => {
      return damage.imagePartDamages;
    });

    const newImageData: ViImageData[] | undefined = state.images?.map((image) => {
      const responseImage = response.images.find(
        (responseImage) => responseImage.id === image.id
      );
      return {
        ...image,
        ...(responseImage ? responseImage : []),
        damages: image.isDuplicate
          ? []
          : expandedImagePartDamages.filter(
              (damage) => damage.imageId === image.id && damage.aggregatedPartDamage.id
            ),
      };
    });

    const [totalLoss] = response.claimTotalLosses || [];
    const [carModel] = response.claimCarModels || [];

    return {
      ...(response.id ? { processingId: response.id } : {}),
      images: newImageData,
      aggregatedPartDamages: aggregatedPartDamages as any,
      claimDamagedParts,
      totalLoss,
      carModel,
      timing: response.timing,
    };
  } catch (e) {
    throw e;
  }
};

export const hashCode = (str: string) => {
  let hash = 0,
    i,
    chr;
  if (str.length === 0) return hash;
  for (i = 0; i < str.length; i++) {
    chr = str.charCodeAt(i);
    hash = (hash << 5) - hash + chr;
    hash |= 0; // Convert to 32bit integer
  }
  return hash;
};

export const groupBy = <T extends { [key: string]: any }>(
  data: T[],
  keys: (string | string[])[],
  fallBackKey: string | undefined = undefined
): Record<string, T> => {
  const digUpValue = (obj: any, [firstKey, ...keys]: (string | string[])[]): any => {
    if (!firstKey && !obj) return undefined;
    const value: any = Array.isArray(firstKey)
      ? digUpValue(obj, firstKey)
      : obj[firstKey];
    if (!value) return undefined;
    return keys.length ? digUpValue(value, keys) : value;
  };
  const res: any = {};
  for (const obj of data) {
    let key: string | undefined = keys
      .map((key) =>
        Array.isArray(key) ? digUpValue(obj, key) : obj ? obj[key] : undefined
      )
      .filter(Boolean)
      .join(" - ");
    key = key || fallBackKey;
    if (!key) break;
    res[key] = [...(res[key] || []), obj];
  }
  return res;
};

export const getPartsToReplace = (
  claimDamagedParts: ViClaimDamagedPart[],
  options?: {
    globalThreshold?: number;
    workActionThreshold?: number;
    physicalPartThreshold?: number;
    isPrimary?: boolean;
  }
): ViPhysicalPart[] => {
  const globalThreshold = options?.globalThreshold || 0.4;
  const workActionThreshold = options?.workActionThreshold || globalThreshold;
  const physicalPartThreshold = options?.physicalPartThreshold || globalThreshold;
  const isPrimary = options?.isPrimary || false;
  return claimDamagedParts
    .filter(
      (cDP) =>
        cDP.p >= globalThreshold &&
        (!cDP.sideResult || cDP.sideResult?.p >= globalThreshold)
    )
    .flatMap((cDP) =>
      cDP.physicalParts.filter(
        (pP) =>
          pP.p >= physicalPartThreshold &&
          pP.workActionResults.some(
            (wA) =>
              wA.workAction === "REPLACE" &&
              (!isPrimary || wA.isPrimary) &&
              (workActionThreshold === undefined || wA.p >= workActionThreshold)
          )
      )
    );
};

export const capitalize = (string?: string) =>
  string
    ? string.replace(
        /(?:\b\w+\b)|(?:\W+)/g,
        (match) => match[0].toUpperCase() + match.slice(1).toLowerCase()
      )
    : undefined;

export const sortByWorkAction = <
  T extends Record<K, ViWorkActionLabel>,
  K extends keyof any
>(
  a: T,
  b: T,
  key: K,
  desc: boolean = true
) => {
  const order: ViWorkActionLabel[] = [
    ViWorkActionLabel["REPLACE"],
    ViWorkActionLabel["REPAIR"],
    ViWorkActionLabel["PAINT"],
    ViWorkActionLabel["REMOVE-REFIT"],
    ViWorkActionLabel["OTHER"],
  ];
  const [A, B] = desc ? [a, b] : [b, a];
  if ([A, B].every((el) => el.hasOwnProperty(key) || typeof el[key] !== "string"))
    return 0;
  return order.findIndex((wA) => A[key] === wA) - order.findIndex((wA) => B[key] === wA);
};

type SetValue<T> = Dispatch<SetStateAction<T>>;
export function useLocalStorage<T>(key: string, initialValue: T): [T, SetValue<T>] {
  const readValue = (): T => {
    if (typeof window === "undefined") {
      return initialValue;
    }

    try {
      const item = window.localStorage.getItem(key);
      return item ? (JSON.parse(item) as T) : initialValue;
    } catch (error) {
      return initialValue;
    }
  };

  const [storedValue, setStoredValue] = useState<T>(readValue);

  const setValue: SetValue<T> = (value) => {
    if (typeof window == "undefined") {
    }

    try {
      const newValue = value instanceof Function ? value(storedValue) : value;

      window.localStorage.setItem(key, JSON.stringify(newValue));

      setStoredValue(newValue);

      window.dispatchEvent(new Event("local-storage"));
    } catch (error) {}
  };

  useEffect(() => {
    setStoredValue(readValue());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handleStorageChange = () => {
      setStoredValue(readValue());
    };

    window.addEventListener("storage", handleStorageChange);

    window.addEventListener("local-storage", handleStorageChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("local-storage", handleStorageChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return [storedValue, setValue];
}

export default useLocalStorage;
